import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs, FreeMode } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/free-mode';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import VehicleImage from '../../components/base/VehicleImage';
import { vehicles } from '../../mocks/vehicles';
import { locations } from '../../mocks/cart';

export default function VehicleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [pickupDate, setPickupDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [pickupLocation, setPickupLocation] = useState(locations[0]);
  const [returnLocation, setReturnLocation] = useState(locations[0]);
  const [rentalDuration, setRentalDuration] = useState<'30min' | '1h' | '3h' | '6h' | '12h' | '24h' | string>('');
  const [days, setDays] = useState(1);

  const vehicle = vehicles.find(v => v.id === id);

  useEffect(() => {
    if (vehicle) {
      const firstDuration = Object.keys(vehicle.pricing)[0] as '30min' | '1h' | '3h' | '6h' | '12h' | '24h';
      setRentalDuration(firstDuration);
    }
  }, [vehicle]);

  useEffect(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    setPickupDate(today.toISOString().split('T')[0]);
    setReturnDate(tomorrow.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (pickupDate && returnDate) {
      const pickup = new Date(pickupDate);
      const returnD = new Date(returnDate);
      const diffTime = Math.abs(returnD.getTime() - pickup.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setDays(diffDays || 1);
    }
  }, [pickupDate, returnDate]);

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Vehículo no encontrado</h2>
          <Link to="/flota" className="text-amber-400 hover:text-amber-500 cursor-pointer">
            Volver a la flota
          </Link>
        </div>
      </div>
    );
  }

  const currentPrice = vehicle.pricing[rentalDuration as keyof typeof vehicle.pricing] ?? 0;
  const totalPrice = rentalDuration === '24h' ? currentPrice * days : currentPrice;

  const handleAddToCart = () => {
    const cartItem = {
      vehicleId: vehicle.id,
      brand: vehicle.brand,
      model: vehicle.model,
      pricePerDay: currentPrice,
      image: vehicle.images[0],
      pickupDate,
      returnDate,
      days: rentalDuration === '24h' ? days : 1,
      duration: rentalDuration,
      pickupLocation,
      returnLocation
    };

    const existingCart = JSON.parse(localStorage.getItem('luxuryCarCart') || '[]');
    existingCart.push(cartItem);
    localStorage.setItem('luxuryCarCart', JSON.stringify(existingCart));

    navigate('/carrito');
  };

  const durationLabels = {
    '30min': '30 Minutos',
    '1h': '1 Hora',
    '3h': '3 Horas',
    '6h': '6 Horas',
    '12h': '12 Horas',
    '24h': '24 Horas'
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <WhatsAppButton />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Breadcrumb */}
          <div className="mb-8 flex items-center gap-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-amber-400 cursor-pointer">Inicio</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/flota" className="hover:text-amber-400 cursor-pointer">Flota</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">{vehicle.brand} {vehicle.model}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <div>
              <Swiper
                modules={[Navigation, Thumbs]}
                navigation
                thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                className="rounded-2xl overflow-hidden mb-4"
              >
                {vehicle.images.map((image, index) => (
                  <SwiperSlide key={index}>
                    <div className="aspect-[4/3] bg-gray-900 relative">
                      <VehicleImage
                        src={image}
                        alt={`${vehicle.brand} ${vehicle.model} - ${index + 1}`}
                        className="w-full h-full object-cover absolute inset-0"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <Swiper
                modules={[FreeMode, Thumbs]}
                onSwiper={setThumbsSwiper}
                spaceBetween={10}
                slidesPerView={3}
                freeMode
                watchSlidesProgress
                className="thumbs-swiper"
              >
                {vehicle.images.map((image, index) => (
                  <SwiperSlide key={index} className="cursor-pointer">
                    <div className="aspect-[4/3] rounded-lg overflow-hidden border-2 border-transparent hover:border-amber-500 transition-all relative bg-gray-900">
                      <VehicleImage
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover absolute inset-0"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* Vehicle Info & Booking */}
            <div>
              <div className="mb-6">
                <span className="inline-block bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  {vehicle.category}
                </span>
                <h1 className="text-4xl lg:text-5xl font-bold text-white mb-2">
                  {vehicle.brand}
                </h1>
                <p className="text-2xl text-gray-300 mb-4">{vehicle.model}</p>
                <div className="flex items-center gap-4 text-3xl font-bold text-amber-400">
                  <span>${currentPrice.toLocaleString()}</span>
                  <span className="text-lg text-gray-400 font-normal">por {durationLabels[rentalDuration]}</span>
                </div>
              </div>

              <p className="text-gray-300 mb-8 leading-relaxed">
                {vehicle.description}
              </p>

              {/* Booking Form */}
              <div className="bg-gray-900 rounded-2xl p-6 mb-8 border border-amber-900/30">
                <h3 className="text-xl font-bold text-white mb-6">Reservar Ahora</h3>
                
                <div className="space-y-4">
                  {/* Duration Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Duración del Alquiler
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {(Object.keys(vehicle.pricing) as Array<keyof typeof vehicle.pricing>).map((duration) => (
                        <button
                          key={duration}
                          onClick={() => setRentalDuration(duration)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                            rentalDuration === duration
                              ? 'bg-amber-500 text-black'
                              : 'bg-black border border-amber-900/30 text-white hover:border-amber-500'
                          }`}
                        >
                          {durationLabels[duration]}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Fecha de Recogida
                      </label>
                      <input
                        type="date"
                        value={pickupDate}
                        onChange={(e) => setPickupDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full bg-black border border-amber-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500"
                      />
                    </div>
                    {rentalDuration === '24h' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Fecha de Devolución
                        </label>
                        <input
                          type="date"
                          value={returnDate}
                          onChange={(e) => setReturnDate(e.target.value)}
                          min={pickupDate}
                          className="w-full bg-black border border-amber-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500"
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Lugar de Recogida
                    </label>
                    <select
                      value={pickupLocation}
                      onChange={(e) => setPickupLocation(e.target.value)}
                      className="w-full bg-black border border-amber-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500 cursor-pointer pr-8"
                    >
                      {locations.map((location) => (
                        <option key={location} value={location}>{location}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Lugar de Devolución
                    </label>
                    <select
                      value={returnLocation}
                      onChange={(e) => setReturnLocation(e.target.value)}
                      className="w-full bg-black border border-amber-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500 cursor-pointer pr-8"
                    >
                      {locations.map((location) => (
                        <option key={location} value={location}>{location}</option>
                      ))}
                    </select>
                  </div>

                  <div className="bg-black rounded-lg p-4 border border-amber-900/30">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">Duración:</span>
                      <span className="text-white font-semibold">{durationLabels[rentalDuration]}</span>
                    </div>
                    {rentalDuration === '24h' && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-400">Días de alquiler:</span>
                        <span className="text-white font-semibold">{days}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">Precio:</span>
                      <span className="text-white font-semibold">${currentPrice.toLocaleString()} MXN</span>
                    </div>
                    <div className="border-t border-amber-900/30 pt-2 mt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-amber-400 font-bold text-lg">Total:</span>
                        <span className="text-amber-400 font-bold text-2xl">${totalPrice.toLocaleString()} MXN</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleAddToCart}
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-black py-4 rounded-full font-bold text-lg hover:from-amber-600 hover:to-amber-700 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Agregar al Carrito
                  </button>

                  <a
                    href={`https://wa.me/522291578562?text=${encodeURIComponent(`Hola! Me gustaría reservar el ${vehicle.brand} ${vehicle.model} por ${durationLabels[rentalDuration]}. Fecha de recogida: ${pickupDate}. Lugar de recogida: ${pickupLocation}. Lugar de devolución: ${returnLocation}. ¿Está disponible?`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-green-500 text-white py-4 rounded-full font-bold text-lg hover:bg-green-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-2xl"></i>
                    Reservar por WhatsApp
                  </a>
                </div>
              </div>

              {/* Quick Specs */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-gray-900 rounded-lg p-4 border border-amber-900/30">
                  <i className="ri-user-line text-amber-400 text-2xl mb-2"></i>
                  <p className="text-gray-400 text-sm">Pasajeros</p>
                  <p className="text-white font-bold">{vehicle.specifications.passengers}</p>
                </div>
                <div className="bg-gray-900 rounded-lg p-4 border border-amber-900/30">
                  <i className="ri-settings-line text-amber-400 text-2xl mb-2"></i>
                  <p className="text-gray-400 text-sm">Transmisión</p>
                  <p className="text-white font-bold">{vehicle.specifications.transmission}</p>
                </div>
                <div className="bg-gray-900 rounded-lg p-4 border border-amber-900/30">
                  <i className="ri-gas-station-line text-amber-400 text-2xl mb-2"></i>
                  <p className="text-gray-400 text-sm">Combustible</p>
                  <p className="text-white font-bold">{vehicle.specifications.fuel}</p>
                </div>
                <div className="bg-gray-900 rounded-lg p-4 border border-amber-900/30">
                  <i className="ri-calendar-line text-amber-400 text-2xl mb-2"></i>
                  <p className="text-gray-400 text-sm">Año</p>
                  <p className="text-white font-bold">{vehicle.specifications.year}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-white mb-8">Características Destacadas</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {vehicle.features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-gray-900 rounded-lg p-4 border border-amber-900/30 flex items-start gap-3"
                >
                  <i className="ri-checkbox-circle-fill text-amber-400 text-xl mt-1"></i>
                  <span className="text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Policies */}
          <div className="mt-16 bg-gray-900 rounded-2xl p-8 border border-amber-900/30">
            <h2 className="text-3xl font-bold text-white mb-6">Información Importante</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-amber-400 mb-4 flex items-center gap-2">
                  <i className="ri-shield-check-line"></i>
                  Requisitos
                </h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-400 mt-1"></i>
                    <span>Licencia de conducir válida (mínimo 25 años)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-400 mt-1"></i>
                    <span>Tarjeta de crédito para depósito de seguridad</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-400 mt-1"></i>
                    <span>Identificación oficial vigente</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-400 mt-1"></i>
                    <span>Comprobante de domicilio</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold text-amber-400 mb-4 flex items-center gap-2">
                  <i className="ri-information-line"></i>
                  Incluye
                </h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-400 mt-1"></i>
                    <span>Seguro de cobertura completa</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-400 mt-1"></i>
                    <span>Kilometraje ilimitado</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-400 mt-1"></i>
                    <span>Asistencia en carretera 24/7</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-amber-400 mt-1"></i>
                    <span>Entrega y recogida en ubicaciones seleccionadas</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-amber-900/30">
              <Link to="/politicas" className="text-amber-400 hover:text-amber-500 font-semibold cursor-pointer">
                Ver todas las políticas y términos →
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}